// Page scripts from docs/kennenlernen/kennenlernen.html (sticky CTA,
// wave / pearl / quote / FAQ entrance, quiz-match diptych, typing h1).
export function initKennenlernenEffects(root, opts) {
  if (!root) {
    return function () {
    };
  }
  const profileUrl = (opts && opts.profileUrl) || function (name) {
    return name;
  };
  const cleanups = [];
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isBot = !!navigator.webdriver;

  function on(el, type, fn, capture) {
    el.addEventListener(type, fn, capture);
    cleanups.push(function () {
      el.removeEventListener(type, fn, capture);
    });
  }

  // Sticky-CTA: appears on mobile once the hero leaves the viewport
  const hero = root.querySelector('#hero');
  const bar = root.querySelector('#sticky-cta');
  if (hero && bar && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      function (entries) {
        bar.classList.toggle('on', !entries[entries.length - 1].isIntersecting);
      },
      { rootMargin: '-30% 0px 0px' }
    );
    io.observe(hero);
    cleanups.push(function () {
      io.disconnect();
    });
  }

  initWave(root, reduced, isBot, cleanups);
  initPearls(root, reduced, isBot, cleanups);
  initVoices(root, reduced, isBot, cleanups);
  initFaq(root, reduced, isBot, cleanups, on);
  initDuo(root, reduced, isBot, cleanups, profileUrl);
  initTyping(root, reduced, isBot, cleanups);

  const failsafe = setTimeout(function () {
    root.querySelectorAll('.reveal').forEach(function (el) {
      el.classList.add('is-in');
    });
    if (!window.__krOk && window.__krForceComplete) {
      window.__krForceComplete();
    }
    if (!window.__kwOk && window.__kwForceComplete) {
      window.__kwForceComplete();
    }
    if (!window.__ksOk && window.__ksForceComplete) {
      window.__ksForceComplete();
    }
    if (!window.__kfOk && window.__kfForceComplete) {
      window.__kfForceComplete();
    }
  }, 2500);
  cleanups.push(function () {
    clearTimeout(failsafe);
  });

  return function teardown() {
    cleanups.forEach(function (fn) {
      try {
        fn();
      } catch (e) {
        /* ignore */
      }
    });
  };
}

function initWave(root, reduced, isBot, cleanups) {
  const kr = root.querySelector('[data-kr]');
  const section = root.querySelector('#so-gehts');
  if (!kr || !section) {
    return;
  }

  function forceComplete() {
    try {
      section.classList.add('kr-done');
      kr.querySelectorAll('.kr-step').forEach(function (li) {
        li.classList.add('lit');
      });
    } catch (e) {
    }
  }

  window.__krForceComplete = forceComplete;
  try {
    if (reduced || isBot) {
      return;
    }
    const mqMobile = matchMedia('(max-width:820px)');
    const steps = Array.prototype.slice.call(kr.querySelectorAll('.kr-step'));
    let played = false;
    const DURATION = 2600;
    const EASE = 'cubic-bezier(.45,.05,.25,1)';

    function visiblePaths() {
      return mqMobile.matches
        ? Array.prototype.slice.call(kr.querySelectorAll('.kr-seg .kr-path'))
        : [kr.querySelector('.kr-main')];
    }

    function prime() {
      visiblePaths().forEach(function (p) {
        if (!p) {
          return;
        }
        const L = p.getTotalLength();
        p.style.transition = 'none';
        p.style.strokeDasharray = L + ' ' + L;
        p.style.strokeDashoffset = L;
      });
    }

    function finale() {
      if (mqMobile.matches) {
        const h = kr.querySelector('.kr-heart-m');
        if (h) {
          h.classList.add('beat');
        }
      }
      const btn = section.querySelector('.k-after .btn-primary');
      if (btn) {
        btn.classList.add('handoff');
      }
    }

    function play() {
      if (played) {
        return;
      }
      played = true;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          const ps = visiblePaths().filter(Boolean);
          let total = 0;
          const lens = ps.map(function (p) {
            const L = p.getTotalLength();
            total += L;
            return L;
          });
          let t = 0;
          ps.forEach(function (p, i) {
            const d = DURATION * lens[i] / total;
            p.style.transition =
              'stroke-dashoffset ' + Math.round(d) + 'ms ' + EASE + ' ' + Math.round(t) + 'ms';
            p.style.strokeDashoffset = 0;
            t += d;
          });
          let litTimes;
          if (mqMobile.matches) {
            litTimes = [0, 1, 2].map(function (i) {
              let acc = 0;
              for (let j = 0; j <= i; j++) {
                acc += lens[j];
              }
              return DURATION * acc / total;
            });
          } else {
            litTimes = [0.21, 0.53, 0.85].map(function (f) {
              return DURATION * f;
            });
          }
          steps.forEach(function (li, i) {
            setTimeout(function () {
              li.classList.add('lit');
            }, litTimes[i]);
          });
          setTimeout(finale, DURATION + 120);
        });
      });
    }

    prime();
    kr.classList.add('kr-anim');
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        function (en) {
          if (en[en.length - 1].isIntersecting) {
            io.disconnect();
            play();
          }
        },
        { threshold: 0.15 }
      );
      io.observe(kr);
      cleanups.push(function () {
        io.disconnect();
      });
    } else {
      play();
    }
    const onMq = function () {
      if (played) {
        forceComplete();
      } else {
        prime();
      }
    };
    if (mqMobile.addEventListener) {
      mqMobile.addEventListener('change', onMq);
      cleanups.push(function () {
        mqMobile.removeEventListener('change', onMq);
      });
    } else {
      mqMobile.addListener(onMq);
    }
    window.__krOk = true;
  } catch (e) {
    forceComplete();
  }
}

function initPearls(root, reduced, isBot, cleanups) {
  const kw = root.querySelector('[data-kw]');
  const section = root.querySelector('#warum');
  if (!kw || !section) {
    return;
  }

  function forceComplete() {
    try {
      section.classList.add('kw-done');
      kw.classList.add('go');
    } catch (e) {
    }
  }

  window.__kwForceComplete = forceComplete;
  try {
    if (reduced || isBot || !('IntersectionObserver' in window)) {
      return;
    }
    const line = kw.querySelector('.kw-line');
    const desktop = matchMedia('(min-width:821px)');
    let played = false;

    function prime() {
      if (!line || !desktop.matches) {
        return;
      }
      const L = line.getTotalLength();
      line.style.transition = 'none';
      line.style.strokeDasharray = L + ' ' + L;
      line.style.strokeDashoffset = L;
    }

    function play() {
      if (played) {
        return;
      }
      played = true;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          kw.classList.add('go');
          if (line && desktop.matches && line.style.strokeDasharray) {
            line.style.transition = 'stroke-dashoffset 1400ms cubic-bezier(.4,0,.2,1) 100ms';
            line.style.strokeDashoffset = '0';
          }
        });
      });
      setTimeout(function () {
        kw.classList.remove('kw-anim');
      }, 2400);
    }

    prime();
    kw.classList.add('kw-anim');
    const io = new IntersectionObserver(
      function (en) {
        if (en[en.length - 1].isIntersecting) {
          io.disconnect();
          play();
        }
      },
      { threshold: 0.25 }
    );
    io.observe(kw);
    cleanups.push(function () {
      io.disconnect();
    });
    const onMq = function () {
      if (played) {
        forceComplete();
      } else {
        prime();
      }
    };
    if (desktop.addEventListener) {
      desktop.addEventListener('change', onMq);
      cleanups.push(function () {
        desktop.removeEventListener('change', onMq);
      });
    } else {
      desktop.addListener(onMq);
    }
    window.__kwOk = true;
  } catch (e) {
    forceComplete();
  }
}

function initVoices(root, reduced, isBot, cleanups) {
  const ks = root.querySelector('[data-ks]');
  const section = root.querySelector('#stimmen');
  if (!ks || !section) {
    return;
  }

  function forceComplete() {
    try {
      section.classList.add('ks-done');
      ks.classList.add('go');
    } catch (e) {
    }
  }

  window.__ksForceComplete = forceComplete;
  try {
    if (reduced || isBot || !('IntersectionObserver' in window)) {
      return;
    }
    let played = false;

    function play() {
      if (played) {
        return;
      }
      played = true;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          ks.classList.add('go');
        });
      });
    }

    ks.classList.add('ks-anim');
    const io = new IntersectionObserver(
      function (en) {
        if (en[en.length - 1].isIntersecting) {
          io.disconnect();
          play();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(ks);
    cleanups.push(function () {
      io.disconnect();
    });
    window.__ksOk = true;
  } catch (e) {
    forceComplete();
  }
}

function initFaq(root, reduced, isBot, cleanups, on) {
  const kf = root.querySelector('[data-kf]');
  const section = root.querySelector('#faq');
  if (!kf || !section) {
    return;
  }

  function openFromHash() {
    try {
      if (/^#faq-/.test(location.hash)) {
        const el = document.getElementById(location.hash.slice(1));
        if (el && el.tagName === 'DETAILS') {
          el.open = true;
        }
      }
    } catch (e) {
    }
  }

  openFromHash();
  on(window, 'hashchange', openFromHash);

  function forceComplete() {
    try {
      section.classList.add('kf-done');
      kf.classList.add('go');
    } catch (e) {
    }
  }

  window.__kfForceComplete = forceComplete;
  try {
    if (reduced || isBot || !('IntersectionObserver' in window)) {
      return;
    }
    let played = false;

    function play() {
      if (played) {
        return;
      }
      played = true;
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          kf.classList.add('go');
        });
      });
    }

    kf.classList.add('kf-anim');
    const io = new IntersectionObserver(
      function (en) {
        if (en[en.length - 1].isIntersecting) {
          io.disconnect();
          play();
        }
      },
      { threshold: 0.2 }
    );
    io.observe(kf);
    cleanups.push(function () {
      io.disconnect();
    });
    window.__kfOk = true;
  } catch (e) {
    forceComplete();
  }
}

function initDuo(root, reduced, isBot, cleanups, profileUrl) {
  const duo = root.querySelector('.k-duo');
  if (!duo) {
    return;
  }
  const faces = [duo.querySelector('.kd-l .kd-face'), duo.querySelector('.kd-r .kd-face')];
  if (!faces[0] || !faces[1]) {
    return;
  }
  try {
    if (reduced || isBot) {
      return;
    }
    const MAENNER = ['stephan', 'jonas', 'thomas', 'lukas'];
    const FRAUEN = ['sophia', 'frau-1', 'frau-2', 'frau-3', 'frau-4', 'frau-5', 'frau-6'];
    const RUNDEN = [
      { q: 'Berge oder Meer?', a: 'Meer' },
      { q: 'Kaffee oder Tee?', a: 'Kaffee' },
      { q: 'Kino oder Couch?', a: 'Couch' },
      { q: 'Pizza oder Pasta?', a: 'Pizza' },
      { q: 'Sommer oder Winter?', a: 'Sommer' },
    ];
    const FADE = 7600;
    const FLIP = 8150;
    const NEXT = 9500;
    const HALF = 460;
    const OFFSET = 400;
    const EASE = 'cubic-bezier(.45,0,.55,1)';
    let mIdx = 0;
    let fIdx = 0;
    let rIdx = 0;
    let timers = [];
    let visible = true;
    const qt = duo.querySelector('.kq-qt');
    const al = duo.querySelector('.kq-al');
    const at = duo.querySelector('.kq-at');

    function preload(name) {
      const j = new Image();
      j.src = profileUrl(name);
    }

    function setFace(face, name) {
      const img = face.querySelector('img');
      if (img) {
        img.src = profileUrl(name);
      }
    }

    function flip(face, name) {
      face.style.transition = 'transform ' + HALF + 'ms ' + EASE;
      face.style.transform = 'rotateY(90deg)';
      setTimeout(function () {
        setFace(face, name);
        face.style.transition = 'none';
        face.style.transform = 'rotateY(-90deg)';
        void face.offsetWidth;
        face.style.transition = 'transform ' + HALF + 'ms ' + EASE;
        face.style.transform = 'rotateY(0deg)';
      }, HALF + 20);
    }

    function armRound() {
      timers.push(
        setTimeout(function () {
          preload(MAENNER[(mIdx + 1) % MAENNER.length]);
          preload(FRAUEN[(fIdx + 1) % FRAUEN.length]);
        }, FLIP - 1800)
      );
      timers.push(
        setTimeout(function () {
          duo.classList.add('kq-fade');
        }, FADE)
      );
      timers.push(
        setTimeout(function () {
          mIdx = (mIdx + 1) % MAENNER.length;
          fIdx = (fIdx + 1) % FRAUEN.length;
          flip(faces[0], MAENNER[mIdx]);
          setTimeout(function () {
            flip(faces[1], FRAUEN[fIdx]);
          }, OFFSET);
        }, FLIP)
      );
      timers.push(
        setTimeout(function () {
          rIdx = (rIdx + 1) % RUNDEN.length;
          if (qt) {
            qt.textContent = RUNDEN[rIdx].q;
          }
          if (al) {
            al.textContent = RUNDEN[rIdx].a;
          }
          if (at) {
            at.textContent = RUNDEN[rIdx].a;
          }
          restart();
        }, NEXT)
      );
    }

    function restart() {
      duo.classList.remove('kq-run');
      duo.classList.remove('kq-fade');
      duo.classList.add('kq-gap');
      void duo.offsetWidth;
      duo.classList.remove('kq-gap');
      duo.classList.add('kq-run');
      armRound();
    }

    function stopp() {
      timers.forEach(clearTimeout);
      timers = [];
    }

    cleanups.push(stopp);
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(function (en) {
        const v = en[en.length - 1].isIntersecting;
        if (v === visible) {
          return;
        }
        visible = v;
        stopp();
        if (v) {
          restart();
        }
      });
      io.observe(duo);
      cleanups.push(function () {
        io.disconnect();
      });
    }
    armRound();
  } catch (e) {
  }
}

function initTyping(root, reduced, isBot, cleanups) {
  const el = root.querySelector('#k-greet');
  if (!el) {
    return;
  }
  const h1 = el.closest('h1');
  try {
    if (reduced || isBot) {
      return;
    }
    const WORDS = ['Hallo.', 'Hey!', 'Na, wie geht’s?', 'Moin!', 'Servus!', 'Hi!'];
    const HOLD = 2400;
    const TYPE = 75;
    const DEL = 40;
    const PAUSE = 350;
    let wordIdx = 0;
    let charIdx = WORDS[0].length;
    let deleting = false;
    let timer = null;
    let running = false;
    el.parentElement.classList.add('k-typing');

    function tick() {
      const word = WORDS[wordIdx];
      if (!deleting && charIdx === word.length) {
        timer = setTimeout(function () {
          deleting = true;
          tick();
        }, HOLD);
        return;
      }
      if (deleting) {
        charIdx--;
        el.textContent = word.slice(0, charIdx);
        if (charIdx === 0) {
          deleting = false;
          wordIdx = (wordIdx + 1) % WORDS.length;
          timer = setTimeout(tick, PAUSE);
          return;
        }
        timer = setTimeout(tick, DEL);
      } else {
        charIdx++;
        el.textContent = word.slice(0, charIdx);
        timer = setTimeout(tick, TYPE);
      }
    }

    function start() {
      if (!running) {
        running = true;
        timer = setTimeout(tick, 0);
      }
    }

    function stop() {
      running = false;
      clearTimeout(timer);
      deleting = false;
      charIdx = WORDS[wordIdx].length;
      el.textContent = WORDS[wordIdx];
    }

    cleanups.push(function () {
      stop();
    });
    if ('IntersectionObserver' in window && h1) {
      const io = new IntersectionObserver(function (en) {
        if (en[en.length - 1].isIntersecting) {
          start();
        } else {
          stop();
        }
      });
      io.observe(h1);
      cleanups.push(function () {
        io.disconnect();
      });
    } else {
      start();
    }
  } catch (e) {
    el.textContent = 'Hallo.';
  }
}
